import type { EnrichmentDetail } from '../ai/enrichmentSeam';
import { linkableSource, type PersonListCheckField, type RecordCheckField } from './recordSuggestions';
import { fill, t, type StringKey } from '../strings';
import { normaliseCompanyName, normaliseText } from './normalise';

/**
 * The licensed answer read whole (decision 6180; T2872): a person's work
 * history, education and skills as one suggestion per entry, with the months
 * and the parts each entry carries, and the words the Suggestions tile and the
 * Person page's Education tile draw them in. 🔨 Nothing here writes: an entry
 * reaches a record only through a person's Accept or Merge.
 */

const MONTH_KEYS: readonly StringKey[] = [
  'wholeAnswer.month.january',
  'wholeAnswer.month.february',
  'wholeAnswer.month.march',
  'wholeAnswer.month.april',
  'wholeAnswer.month.may',
  'wholeAnswer.month.june',
  'wholeAnswer.month.july',
  'wholeAnswer.month.august',
  'wholeAnswer.month.september',
  'wholeAnswer.month.october',
  'wholeAnswer.month.november',
  'wholeAnswer.month.december',
];

/** A month as a person reads it: a year alone stays a year, and a year and month reads as September 2015. */
export function monthWords(month: string | null | undefined): string | null {
  if (!month) return null;
  const match = /^(\d{4})(?:-(\d{2}))?/.exec(month);
  if (!match) return null;
  if (match[2] === undefined) return match[1]!;
  const key = MONTH_KEYS[Number(match[2]) - 1];
  return key ? `${t(key)} ${match[1]}` : match[1]!;
}

/** An entry's span in words: from its start to its end, or to now while it is current. */
export function spanWords(start: string | null | undefined, end: string | null | undefined, current = false): string {
  const from = monthWords(start);
  const to = monthWords(end) ?? (current ? t('wholeAnswer.span.now') : null);
  if (from && to) return fill(t('wholeAnswer.span.fromTo'), { from, to });
  if (from) return fill(t('wholeAnswer.span.from'), { from });
  return to ? fill(t('wholeAnswer.span.to'), { to }) : '';
}

/** 🔒 Which entry a list row is (decision 6180): the employer or the school folded with its start month, or the skill's folded name. */
export function entryItemKey(field: PersonListCheckField, value: string, detail: EnrichmentDetail | null | undefined): string {
  if (field === 'Skill') return normaliseText(value);
  if (field === 'JobHistory') return `${normaliseCompanyName(detail?.employer ?? value)}|${detail?.start ?? ''}`;
  return `${normaliseText(detail?.school ?? value)}|${detail?.start ?? ''}`;
}

/** Whether a held row is the same employer or school as an entry, which is what offers Merge. */
export function sameEmployer(left: string, right: string): boolean {
  return normaliseCompanyName(left) !== '' && normaliseCompanyName(left) === normaliseCompanyName(right);
}
export function sameSchool(left: string, right: string): boolean {
  return normaliseText(left) !== '' && normaliseText(left) === normaliseText(right);
}

/** 🔒 A list entry's one line, the suggestion the tile reads: the title at the employer with its span, the school with its degree, or the skill. */
export function historyEntryLine(field: PersonListCheckField, value: string, detail: EnrichmentDetail | null | undefined): string {
  if (field === 'Skill' || !detail) return value;
  if (field === 'JobHistory') {
    const what = detail.title && detail.employer ? fill(t('wholeAnswer.titleAtEmployer'), { title: detail.title as string, employer: detail.employer as string }) : (detail.title || detail.employer || '');
    const span = spanWords(detail.start, detail.end, detail.current === 'true');
    return [what || value, span].filter(Boolean).join(', ');
  }
  const what = [detail.school, [detail.degree, detail.fieldOfStudy].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  const span = spanWords(detail.start, detail.end);
  return [what || value, span].filter(Boolean).join(', ');
}

/** 🔒 A held employment as the tile's Here reads it: the company and its span by month. */
export function heldEmploymentLine(companyName: string, startDay: string, endDay: string | null): string {
  return `${companyName}, ${spanWords(startDay.slice(0, 7), endDay ? endDay.slice(0, 7) : null, endDay === null)}`;
}

/** A held education row as the tile's Here reads it. */
export function heldEducationLine(row: { school: string; degree?: string | null; fieldOfStudy?: string | null; startMonth?: string | null; endMonth?: string | null }): string {
  return historyEntryLine('Education', row.school, {
    school: row.school,
    ...(row.degree ? { degree: row.degree } : {}),
    ...(row.fieldOfStudy ? { fieldOfStudy: row.fieldOfStudy } : {}),
    ...(row.startMonth ? { start: row.startMonth } : {}),
    ...(row.endMonth ? { end: row.endMonth } : {}),
  });
}

/** 🔒 Whether a month and a held day name the same month, or the same year where the month is a year alone. */
export function sameMonth(month: string | null | undefined, day: string | null | undefined): boolean {
  if (!month || !day) return !month && !day;
  return day.startsWith(month.length === 4 ? `${month}-` : month);
}

/** 💰 An answer gives a month or a year, so an employment it dates starts on the first day of it (decision 6180). */
export function firstDayOfMonth(month: string): string {
  return month.length === 4 ? `${month}-01-01` : `${month}-01`;
}

/** And ends on the last day of it. */
export function lastDayOfMonth(month: string): string {
  if (month.length === 4) return `${month}-12-31`;
  const [year, number] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, '0')}`;
}

/** 🔨 A start or an end a person types on the Education tile is a year or a year and month, or blank. */
export function educationMonthProblem(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  if (text === '') return null;
  return /^\d{4}(-(0[1-9]|1[0-2]))?$/.test(text) ? null : t('wholeAnswer.monthProblem');
}

/** One line under a suggestion: what the part is, its words, and its address where it is a link. */
export interface DetailLine {
  label: string;
  value: string;
  /** An HTTPS or HTTP address opened in a new tab; a LinkedIn page is a link nothing here fetches (decision 5381). */
  link: string | null;
}

const DETAIL_LABELS: Readonly<Record<string, StringKey>> = {
  employer: 'wholeAnswer.label.employer', title: 'wholeAnswer.label.title', website: 'wholeAnswer.label.website', linkedIn: 'wholeAnswer.label.linkedIn',
  school: 'wholeAnswer.label.school', degree: 'wholeAnswer.label.degree', fieldOfStudy: 'wholeAnswer.label.fieldOfStudy',
  street: 'wholeAnswer.label.street', suite: 'wholeAnswer.label.suite', locality: 'wholeAnswer.label.locality', region: 'wholeAnswer.label.region', postalCode: 'wholeAnswer.label.postalCode',
};

/** The order each field's parts are drawn in; the months are drawn as one span. */
const DETAIL_ORDER: Readonly<Partial<Record<RecordCheckField, readonly string[]>>> = {
  JobHistory: ['employer', 'title', 'span', 'website', 'linkedIn'],
  Education: ['school', 'degree', 'fieldOfStudy', 'span', 'linkedIn'],
  Address: ['street', 'suite', 'locality', 'region', 'postalCode'],
};

/** 🔒 The parts a suggestion carries, as the tile draws them beneath it (decision 6180). */
export function detailLines(field: RecordCheckField, detail: EnrichmentDetail | null | undefined): DetailLine[] {
  const order = DETAIL_ORDER[field];
  if (!order || !detail) return [];
  const out: DetailLine[] = [];
  for (const part of order) {
    if (part === 'span') {
      const span = spanWords(detail.start, detail.end, detail.current === 'true');
      if (span) out.push({ label: t('wholeAnswer.label.when'), value: span, link: null });
      continue;
    }
    const value = detail[part];
    if (!value) continue;
    const link = part === 'linkedIn' || part === 'website' ? linkableSource(/^https?:\/\//i.test(value) ? value : `https://${value}`) : null;
    out.push({ label: DETAIL_LABELS[part] ? t(DETAIL_LABELS[part]!) : part, value, link });
  }
  return out;
}
